<template>
  <div>
    <!-- Media -->
    <b-media class="mb-2">
      <template #aside>
        <b-avatar
            ref="previewEl"
            :src="bidLocal.avatar"
            :text="avatarText(bidLocal.offer_code)"
            :variant="`light-${resolveBidTypeVariant(bidLocal.offer_code)}`"
            rounded
            size="90px"
        />
      </template>
      <h4 class="d-flex align-items-center mb-1">
        {{ bidLocal.offer_code }}
      </h4>
    </b-media>
    <hr/>
    <!-- Project Info: Input Fields -->
    <b-form @submit.prevent="onSubmit" @reset.prevent="resetForm">
      <b-row class="my-2">
        <b-col cols="12">
          <span class="font-weight-bold">Informations Générales</span>
        </b-col>
      </b-row>
      <b-row class="mb-2">
        <!-- Field: Code -->
        <b-col cols="12" md="4">
          <b-form-group label="Code Offre" label-for="offer_code">
            <b-form-input id="offer_code" v-model="bidLocal.offer_code" disabled/>
          </b-form-group>
        </b-col>

        <!-- Field: Customer -->
        <b-col cols="12" md="4">
          <b-form-group label="Client" label-for="customer">
            <v-select
                v-model="bidLocal.customer"
                :clearable="false"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="customers"
                :reduce="customer => customer.id"
                :taggable="false"
                input-id="customer"
                label="name"
                placeholder="Choisir un client existant"
            >
              <template #no-options>Aucun client trouvé.</template>
            </v-select>
          </b-form-group>
        </b-col>

        <!-- Field: Lessor -->
        <b-col cols="12" md="4">
          <b-form-group label="Bailleur" label-for="lessor">
            <b-form-input id="lessor" v-model="bidLocal.details.lessor"/>
          </b-form-group>
        </b-col>

        <!-- Field: Type -->
        <b-col cols="12" md="4">
          <b-form-group label="Type" label-for="bid-type">
            <v-select
                v-model="bidLocal.type"
                :clearable="false"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="typeOptions"
                :reduce="(type) => type.value"
                input-id="bid-type"
            />
          </b-form-group>
        </b-col>

        <!-- Field: Status -->
        <b-col cols="12" md="4">
          <b-form-group label="Status" label-for="bid-status">
            <v-select
                v-model="bidLocal.status"
                :clearable="false"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="statusOptions"
                :reduce="(status) => status.key"
                input-id="bid-status"
            />
          </b-form-group>
        </b-col>

        <!-- Field: Concerned Department -->
        <b-col cols="12" md="4">
          <b-form-group
              label="Département Concerné"
              label-for="concerned_department"
          >
            <department-select v-model="bidLocal.details.concerned_department" :departments="departmentsOptions"/>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="4">
          <b-row>
            <!-- Field: Deposit Date -->
            <b-col cols="12">
              <b-form-group label="Date de Dépôt" label-for="deposit_date">
                <flat-pickr
                    id="deposit_date"
                    v-model="bidLocal.details.deposit_date"
                    class="form-control"
                />
              </b-form-group>
            </b-col>

            <!-- Field: Deposit Place -->
            <b-col cols="12">
              <b-form-group label="Lieu de Dépôt" label-for="deposit_place">
                <b-form-input
                    id="deposit_place"
                    v-model="bidLocal.details.deposit_place"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>

        <!-- Field: Brief -->
        <b-col cols="12" md="8">
          <b-form-group label="Description" label-for="project-description">
            <quill-editor
                id="quil-content"
                v-model="bidLocal.brief"
                :options="editorOption"
                class="border-bottom-0"
            />
            <div
                id="quill-toolbar"
                class="d-flex justify-content-end border-top-0"
            >
              <!-- Add a bold button -->
              <button class="ql-bold"/>
              <button class="ql-italic"/>
              <button class="ql-underline"/>
              <button class="ql-align"/>
              <button class="ql-link"/>
            </div>
          </b-form-group>
        </b-col>
      </b-row>

      <hr/>

      <!-- Action Buttons -->
      <b-button
          :block="$store.getters['app/currentBreakPoint'] === 'xs'"
          :disabled="isLoading"
          class="mb-1 mb-sm-0 mr-0 mr-sm-1"
          type="submit"
          variant="primary"
      >
        <b-spinner v-if="isLoading" small></b-spinner>
        Sauvegarder
      </b-button>
      <b-button
          :block="$store.getters['app/currentBreakPoint'] === 'xs'"
          variant="outline-secondary"
          @click="() => $router.go(-1)"
      >
        Retour
      </b-button>
    </b-form>
  </div>
</template>

<script>
import DepartmentSelect from "@/components/departments/DepartmentSelect.vue";
import {BAvatar, BButton, BCol, BForm, BFormGroup, BFormInput, BMedia, BRow, BSpinner,} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import {quillEditor} from "vue-quill-editor";
import {avatarText} from "@core/utils/filter";
import vSelect from "vue-select";
import {useInputImageRenderer} from "@core/comp-functions/forms/form-utils";
import {onMounted, ref, watch} from "@vue/composition-api";
import useBidsList from "@/views/apps/bid/bids-list/useBidsList";
import store from "@/store";
import {useRouter} from "@core/utils/utils";
import {typeOptions} from "@/constants/bid";

export default {
  components: {
    DepartmentSelect,
    BButton,
    BMedia,
    BAvatar,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BSpinner,
    vSelect,
    flatPickr,
    quillEditor,
  },
  props: {
    bidData: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const {router} = useRouter();
    const isLoading = ref(false);

    onMounted(() => {
      let props = ["customers", "countries", "departments", "bid_status", "bid_type"];
      fetchBidProperties(props);
    });

    const {
      fetchBidProperties,
      resolveBidTypeVariant,
      customerOptions,
      countryOptions,
      departmentsOptions,
      statusOptions,

    } = useBidsList();

    const editorOption = {
      modules: {
        toolbar: "#quill-toolbar",
      },
      placeholder: "Écrivez une description breve du projet",
    };

    // ? Demo Purpose => Update image on click of update
    const refInputEl = ref(null);
    const previewEl = ref(null);

    const {inputImageRenderer} = useInputImageRenderer(
        refInputEl,
        (base64) => {
          // eslint-disable-next-line no-param-reassign
          props.bidData.avatar = base64;
        }
    );

    const bidLocal = ref(JSON.parse(JSON.stringify(props.bidData)));

    const resetForm = () => {
      bidLocal.value = JSON.parse(JSON.stringify(props.bidData));
    };

    watch(
        bidLocal,
        (newBidData) => {
          if (newBidData !== undefined) {
            if (
                newBidData.hasOwnProperty("customer") &&
                typeof newBidData.customer === "object"
            ) {
              newBidData.customer = newBidData.customer?.id;
            }
          }
        },
        {
          deep: true,
          immediate: true,
        }
    );

    watch(
        props.bidData,
        () => {
          resetForm();
        },
        {deep: true}
    );

    const onSubmit = () => {
      isLoading.value = true;

      store
          .dispatch("bid/updateBid", bidLocal.value)
          .then((response) => {
            bidLocal.value = response.resource;
            isLoading.value = false;
            router.go(-1);
          })
          .catch(() => isLoading.value = false);
    };

    return {
      isLoading,
      resolveBidTypeVariant,
      avatarText,

      typeOptions,
      statusOptions,
      countryOptions,
      departmentsOptions,

      customers: customerOptions,
      bidLocal,

      resetForm,
      onSubmit,

      // UI
      editorOption,

      //  ? Demo - Update Image on click of update button
      refInputEl,
      previewEl,
      inputImageRenderer,
    };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/map-leaflet.scss";
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/libs/quill.scss";
</style>

<style lang="scss" scoped>
@import "~@core/scss/base/bootstrap-extended/include";

.assignee-selector {
  ::v-deep .vs__dropdown-toggle {
    padding-left: 0;
  }
}

#quil-content ::v-deep {
  > .ql-container {
    border-bottom: 0;
  }

  + #quill-toolbar {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: $border-radius;
    border-bottom-right-radius: $border-radius;
  }
}
</style>
